import get from 'lodash/get';

import PaymentModel from 'app/src/data/models/Payment';
import { BaseStripeTerminal } from 'app/src/payments/terminals/BaseStripeTerminal';
import Terminal from 'app/src/payments/terminals/Terminal';

export default class CloudStripeTerminal extends BaseStripeTerminal {
  static _MAXIMUM_POLLING_TIME_SERVER_SIDE = 120000; // 120 seconds
  static _MAXIMUM_POLLING_TIME_BEFORE_RESOLVE = 30000; // 30 seconds
  constructor(terminalModel) {
    super(terminalModel, undefined, CloudStripeTerminal._MAXIMUM_POLLING_TIME_SERVER_SIDE);
    this._paymentProcessingRolloutEnabled = false;
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   * @param {Boolean} paymentOptions.isManualEntry  Optional - when true the terminal will be prompt for a manual
   *                                 entry (when available)
   * @param {Boolean} paymentOptions.isUnreferencedRefund Optional - when true the unreferenced refund flow will be
   *                                                      triggered
   */
  processPayment(paymentOptions = {}) {
    return super.processPayment({
      ...paymentOptions,
      integrationType: PaymentModel.IntegrationType.SERVER,
    });
  }

  pollApiForPaymentStatus(
    payment,
    pollingTimeBeforeFirstResolve = CloudStripeTerminal._MAXIMUM_POLLING_TIME_BEFORE_RESOLVE
  ) {
    return super.pollApiForPaymentStatus(payment, pollingTimeBeforeFirstResolve);
  }

  /**
   * Helper function to retrieve the status of the payment from the API.  Once the status is no longer
   * pending the promise will be resolved or rejected accordingly.
   *
   * @param {Payment} payment     The payment whose status is currently unknown
   *                              when the status of the payment has been resolved.
   * @private
   */
  async _getPaymentStatus(payment) {
    const updatedPayment = await super._getPaymentStatus(payment);

    if (!updatedPayment) {
      return;
    }

    const paymentStatus = updatedPayment.get('status');
    if (
      paymentStatus === PaymentModel.Status.APPROVED ||
      paymentStatus === PaymentModel.Status.PRE_AUTHORIZED ||
      paymentStatus === PaymentModel.Status.CANCELLED ||
      paymentStatus === PaymentModel.Status.DECLINED ||
      paymentStatus === PaymentModel.Status.ERROR
    ) {
      return updatedPayment;
    }

    if (paymentStatus === PaymentModel.Status.PENDING) {
      const paymentToken = get(
        updatedPayment.get('cardPaymentRequest'),
        'parameters.paymentToken',
        null
      );

      // if an unreferenced refund, we want to update the terminal payment modal to enable the cancel button
      const isUnreferencedRefund =
        updatedPayment.get('operationType') === PaymentModel.OperationType.SALE &&
        updatedPayment.get('refundedPaymentId') === undefined &&
        updatedPayment.get('totalAmount').isNegative();

      if (this._paymentProcessingRolloutEnabled && !this._shouldPoll) {
        return;
      }

      if ((paymentToken && !this.terminalResolvedPayment) || isUnreferencedRefund) {
        const event = new CustomEvent(Terminal.TerminalEvents.PAYMENT_STARTED, {
          detail: { payment: updatedPayment },
        });
        window.dispatchEvent(event);
      }
      return;
    }
    throw new Error('Unknown payment status');
  }
}
